import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useAuth";
import { useAppStore } from "@/store/useAppStore";
import { useIsSolo } from "@/hooks/useIsSolo";
import { fetchAccounts } from "@/lib/queries";
import { fetchAccountLiquidityByCurrencyScoped, fetchCcDebtByAccountScoped } from "@/lib/cc";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCIES } from "@/lib/currencies";
import { useFormatCurrency } from "@/hooks/useFormatCurrency";
import { toast } from "sonner";
import { toastError } from "@/lib/errors";
import { Plus, Trash2, Pencil, Scale, Lock, Home, Building2, Archive, ArchiveRestore } from "lucide-react";
import { BalanceAdjustDialog } from "@/components/BalanceAdjustDialog";
import { ConciliarBancoDialog } from "@/components/ConciliarBancoDialog";
import { accountScope, isHouseholdAccount } from "@/lib/accountDisplay";
import {
  accountLiquidityQueryKey,
  accountsQueryKey,
  archivedAccountsQueryKey,
  ccDebtByAccountQueryKey,
  creditCardDetailsQueryKey,
  householdQueryKey,
  institutionAccountQueryKeys,
} from "@/lib/authScopedQueryKeys";
import {
  financialInstitutionOptionsForAccountType,
  getFinancialInstitution,
} from "@/lib/financialInstitutions";
import {
  fetchCreditCardDetailsRows,
  resolveCreditCardEditState,
  shouldWaitForCreditCardDetails,
  type CreditCardDetailsSupabaseClient,
} from "@/lib/creditCardEditGate";

type Account = Database["public"]["Tables"]["accounts"]["Row"];
type AccountType = Account["type"];

// B2a: los errores del trigger de cuentas de ahorro ya vienen en español y dicen qué regla se rompió
// ("no podés colgar un ahorro de la cuenta de tu pareja"…): se muestran tal cual en vez del genérico.
function accountSaveError(error: unknown) {
  const code = (error as { code?: string } | null)?.code;
  const message = (error as { message?: string } | null)?.message ?? "";
  const isSavingsRule =
    (code === "23514" || code === "23503") && /ahorro/i.test(message) && !/violates/i.test(message);
  if (isSavingsRule) {
    toast.error(message);
    return;
  }
  toastError(error);
}

// B2a: marca "Ahorro · en <cuenta>" de la lista. Si la cuenta de la que cuelga no se ve, no inventa nombre.
function SavingsTag({ account, accounts }: { account: Account; accounts: Account[] }) {
  const parent = accounts.find((p) => p.id === account.savings_parent_account_id);
  return (
    <div className="mt-0.5 text-[10px] text-primary">
      Ahorro{account.savings_parent_account_id ? ` · en ${parent?.name ?? "otra cuenta"}` : ""}
    </div>
  );
}

function renderBankConciliationButton(account: Account, onSelect: (account: Account) => void) {
  // Una cuenta de ahorro (Cajita) no se concilia sola: su extracto es el del banco donde vive.
  if (account.type !== "bank" || account.is_savings) return null;
  return (
    <Button size="sm" variant="outline" className="min-h-9" onClick={() => onSelect(account)}>
      Conciliar
    </Button>
  );
}

function BankConciliationDialog({
  account,
  onClose,
}: {
  account: Account | null;
  onClose: () => void;
}) {
  return (
    <ConciliarBancoDialog
      account={account}
      open={!!account}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    />
  );
}

// F5-B: permite llegar desde Tarjetas con "Editar" y abrir directo el diálogo
// de edición de la cuenta/tarjeta correspondiente, sin buscar el lápiz acá.
const accountsSearchSchema = z.object({
  editAccountId: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/_app/accounts")({
  validateSearch: zodValidator(accountsSearchSchema),
  component: AccountsPage,
});

function AccountsPage() {
  const formatCurrency = useFormatCurrency();
  const navigate = useNavigate();
  const editAccountIdFromSearch = Route.useSearch().editAccountId;
  const { profile } = useProfile();
  const hh = profile?.household_id;
  const profileId = profile?.id;
  const qc = useQueryClient();
  const { viewMode } = useAppStore();
  const { isSolo } = useIsSolo();
  const accQ = useQuery({
    queryKey: accountsQueryKey(hh ?? "", profileId ?? ""),
    queryFn: () => fetchAccounts(hh!),
    enabled: !!hh && !!profileId,
  });
  const scope: "household" | "personal" =
    isSolo ? "household" : (viewMode === "personal" ? "personal" : "household");
  const liquidityQ = useQuery({
    queryKey: accountLiquidityQueryKey(hh ?? "", profileId ?? "", scope),
    queryFn: () => fetchAccountLiquidityByCurrencyScoped(scope),
    enabled: !!hh && !!profileId,
  });
  const debtQ = useQuery({
    queryKey: ccDebtByAccountQueryKey(hh ?? "", profileId ?? "", scope),
    queryFn: () => fetchCcDebtByAccountScoped(scope),
    enabled: !!hh && !!profileId,
  });
  const ccQ = useQuery({
    queryKey: creditCardDetailsQueryKey(hh ?? "", profileId ?? ""),
    queryFn: () => {
      const ids = (accQ.data ?? []).filter((a) => a.type === "credit_card").map((a) => a.id);
      // El cast evita que tsc intente unificar el tipo generado gigante de
      // SupabaseClient<Database> contra la interfaz angosta de abajo (deep
      // instantiation) — la forma real que usamos (.from().select().in())
      // es la misma, solo se angosta para poder mockearla en los tests.
      return fetchCreditCardDetailsRows(
        supabase as unknown as CreditCardDetailsSupabaseClient,
        ids,
      );
    },
    enabled: !!accQ.data && !!profileId,
  });
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adjustAccount, setAdjustAccount] = useState<any | null>(null);
  const [reconcileAccount, setReconcileAccount] = useState<Account | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [hasMovementsAccountId, setHasMovementsAccountId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  function closeReconciliation() {
    setReconcileAccount(null);
  }

  const archivedQ = useQuery({
    queryKey: archivedAccountsQueryKey(hh ?? "", profileId ?? ""),
    queryFn: async () => {
      const { data, error } = await supabase.from("accounts").select("*").eq("household_id", hh!).eq("is_active", false).order("name");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!hh && !!profileId && showArchived,
  });

  const householdQ = useQuery({
    queryKey: householdQueryKey(hh ?? "", profileId ?? ""),
    queryFn: async () => {
      const { data, error } = await supabase.from("households").select("base_currency").eq("id", hh!).single();
      if (error) throw error;
      return data;
    },
    enabled: !!hh && !!profileId,
  });
  const householdCurrency = householdQ.data?.base_currency ?? "MXN";

  async function refreshAccountQueries() {
    if (!hh || !profileId) return;
    await Promise.all(
      institutionAccountQueryKeys(hh, profileId).map((queryKey) =>
        qc.invalidateQueries({ queryKey, exact: true }),
      ),
    );
  }

  const [name, setName] = useState("");
  const [type, setType] = useState<AccountType>("bank");
  const [currency, setCurrency] = useState("MXN");
  const [institutionCode, setInstitutionCode] = useState("none");
  // B2a: cuenta de ahorro (Cajita/Apartado). `savingsParentId`: "" = sin elegir (no se guarda así) ·
  // "none" = Ninguna (ahorro suelto) · uuid = cuenta bancaria donde vive.
  const [isSavings, setIsSavings] = useState(false);
  const [savingsParentId, setSavingsParentId] = useState("");

  useEffect(() => {
    if (householdQ.data?.base_currency) setCurrency(householdQ.data.base_currency);
  }, [householdQ.data?.base_currency]);

  // F5-B: llegó desde Tarjetas con "Editar" (?editAccountId=...) — abrir el
  // diálogo de edición directo, sin obligar a buscar el lápiz en esta lista.
  // Fix rebote de auditoría: si el target es una tarjeta, hay que esperar a
  // que ccQ resuelva (éxito o error) antes de abrir — abrirlo en el mismo
  // commit en que ccQ recién se habilita puebla día de corte/vencimiento/
  // límite con los defaults del formulario en vez de los reales, y Guardar
  // los pisa. Mientras se espera NO se limpia el search param, así el efecto
  // vuelve a correr solo cuando ccQ.isSuccess/isError cambie.
  // Solo debe reaccionar al parámetro de búsqueda, a la lista de cuentas y al
  // estado de ccQ — no a `navigate`/`openEdit` (nuevos en cada render;
  // incluirlos reabriría el diálogo en cada re-render).
  useEffect(() => {
    if (!editAccountIdFromSearch || !accQ.data) return;
    const target = accQ.data.find((a) => a.id === editAccountIdFromSearch);
    if (target && shouldWaitForCreditCardDetails(target.type, ccQ.isSuccess || ccQ.isError)) {
      return;
    }
    if (target) openEdit(target);
    navigate({ to: "/accounts", search: { editAccountId: undefined }, replace: true });
  }, [editAccountIdFromSearch, accQ.data, ccQ.isSuccess, ccQ.isError]); // eslint-disable-line react-hooks/exhaustive-deps
  const [isShared, setIsShared] = useState(true);
  const [isHouseholdAcc, setIsHouseholdAcc] = useState(false);
  const [openingBalance, setOpeningBalance] = useState("0");
  const [openingDate, setOpeningDate] = useState(`${new Date().getFullYear() - 1}-12-31`);
  const [closingDay, setClosingDay] = useState("1");
  const [dueDay, setDueDay] = useState("15");
  const [creditLimit, setCreditLimit] = useState("0");
  // F5-B fix: false mientras no confirmamos que closingDay/dueDay/creditLimit
  // vienen de credit_card_details real (no de los defaults del formulario).
  // Ver src/lib/creditCardEditGate.ts.
  const [ccDetailsReady, setCcDetailsReady] = useState(true);

  function openNew() {
    setEditingId(null);
    setName(""); setType("bank"); setCurrency(householdCurrency);
    setInstitutionCode("none");
    setIsSavings(false);
    setSavingsParentId("");
    setIsShared(true);
    setIsHouseholdAcc(false);
    setOpeningBalance("0"); setOpeningDate(`${new Date().getFullYear() - 1}-12-31`);
    setClosingDay("1"); setDueDay("15"); setCreditLimit("0");
    setCcDetailsReady(true);
    setOpen(true);
  }

  async function openEdit(a: any) {
    setEditingId(a.id);
    setName(a.name); setType(a.type); setCurrency(a.currency);
    setInstitutionCode(a.institution_code ?? "none");
    setIsSavings(a.is_savings === true);
    setSavingsParentId(a.is_savings === true ? (a.savings_parent_account_id ?? "none") : "");
    setIsShared(a.is_shared !== false);
    setIsHouseholdAcc(a.is_household_account === true);
    setOpeningBalance("");
    setOpeningDate(`${new Date().getFullYear() - 1}-12-31`);
    if (a.type === "credit_card") {
      const resolved = resolveCreditCardEditState(a.id, ccQ.isSuccess, ccQ.data ?? []);
      setClosingDay(resolved.closingDay);
      setDueDay(resolved.dueDay);
      setCreditLimit(resolved.creditLimit);
      setCcDetailsReady(resolved.ccDetailsReady);
    } else {
      setCcDetailsReady(true);
    }
    setOpen(true);
  }

  async function upsertOpeningBalance(accountId: string, amount: number): Promise<string | null> {
    if (!hh || !profile) return null;
    if (!openingDate) return "La fecha del saldo inicial es requerida";

    const { data: existing } = await supabase
      .from("transaction_headers")
      .select("id")
      .eq("account_id", accountId)
      .eq("description", "Saldo inicial")
      .maybeSingle();

    const { data: cats } = await supabase.from("categories").select("id, type").eq("household_id", hh).eq("is_active", true);
    let cat = cats?.find((c) => c.type === (amount >= 0 ? "income" : "expense"));

    if (!cat) {
      const { data: newCat, error: catError } = await supabase.from("categories").insert({
        household_id: hh, name: "Saldo inicial", type: amount >= 0 ? "income" : "expense",
        color: "#64748b", icon: "Wallet", visibility: "shared",
      }).select().single();
      if (catError) return `No se pudo crear categoría: ${catError.message}`;
      cat = newCat ?? undefined;
    }
    if (!cat) return "No se encontró categoría para el saldo inicial";

    if (existing) {
      const { error: hErr } = await supabase.from("transaction_headers").update({
        total_amount: Math.abs(amount), date: openingDate,
      }).eq("id", existing.id);
      if (hErr) return `Error al actualizar saldo inicial: ${hErr.message}`;

      const { data: existingLine } = await supabase.from("transaction_lines").select("*").eq("header_id", existing.id).maybeSingle();
      if (existingLine) {
        const { error: lErr } = await supabase.from("transaction_lines").update({ amount: Math.abs(amount), category_id: cat.id }).eq("id", existingLine.id);
        if (lErr) return `Error al actualizar línea de saldo: ${lErr.message}`;
      }
    } else {
      // Rebote #170 ronda 4, FIX 1: `.select().single()` sin columnas pide "*" e implica leer
      // refund_of_header_id (permission denied). Sólo se usa `header.id` más abajo.
      const { data: header, error: hErr } = await supabase.from("transaction_headers").insert({
        household_id: hh, created_by: profile.id, account_id: accountId, date: openingDate,
        total_amount: Math.abs(amount), description: "Saldo inicial", type: "regular", exchange_rate: 1,
      }).select("id").single();
      if (hErr || !header) return `Error al crear saldo inicial: ${hErr?.message ?? "sin respuesta"}`;

      const { error: lErr } = await supabase.from("transaction_lines").insert({
        header_id: header.id, category_id: cat.id, amount: Math.abs(amount),
        visibility: "shared", owner_id: profile.id,
      });
      if (lErr) return `Error al crear línea de saldo: ${lErr.message}`;
    }
    return null;
  }

  // B2a: cuentas donde puede vivir el ahorro que se está editando (mismas reglas que el trigger): banco o
  // efectivo que no sea de ahorro, no la cuenta misma, del mismo ámbito (hogar/personal) y, si es personal,
  // mía. Sin dato de la lista no se ofrece ninguna (nada se adivina).
  const savingsAllowed = type === "bank" || type === "cash";
  const savingsActive = savingsAllowed && isSavings;
  const savingsWillBeHousehold = !isSolo && isHouseholdAcc && isShared;
  const savingsOwnerId = editingId
    ? ((accQ.data ?? []).find((a) => a.id === editingId)?.owner_id ?? profileId)
    : profileId;
  const savingsParentOptions = (accQ.data ?? []).filter(
    (a) =>
      (a.type === "bank" || a.type === "cash") &&
      !a.is_savings &&
      a.id !== editingId &&
      (a.is_household_account === true) === savingsWillBeHousehold &&
      (savingsWillBeHousehold || a.owner_id === savingsOwnerId),
  );

  // Sólo bank/cash llevan los campos de ahorro; en una tarjeta no se manda nada (como antes de B2a).
  const savingsFields = savingsAllowed
    ? {
        is_savings: savingsActive,
        savings_parent_account_id:
          savingsActive && savingsParentId !== "none" ? savingsParentId : null,
      }
    : {};

  async function handleSave() {
    if (!hh || !profile || isSaving) return;
    if (savingsActive) {
      if (savingsParentId === "") {
        return toast.error(
          "Elegí en qué cuenta vive el ahorro (o «Ninguna» si no cuelga de ninguna).",
        );
      }
      const parentStillValid = savingsParentOptions.some((a) => a.id === savingsParentId);
      if (savingsParentId !== "none" && !parentStillValid) {
        return toast.error(
          "La cuenta donde vive el ahorro ya no corresponde (cambió el ámbito o la privacidad). Elegila de nuevo.",
        );
      }
    }
    setIsSaving(true);
    const normalizedInstitutionCode =
      type === "bank" || type === "credit_card"
        ? institutionCode === "none"
          ? null
          : institutionCode
        : null;
    if (editingId) {
      // UPDATE
      const updatePayload: any = { name, currency, institution_code: normalizedInstitutionCode };
      // Solo permitimos cambiar privacidad si la cuenta es del usuario actual
      // (o si quien la creó soy yo). owner_id es inmutable por trigger.
      Object.assign(updatePayload, savingsFields);
      if (!isSolo) {
        updatePayload.is_shared = isShared;
        // Cuenta del hogar implica is_shared = true (validado también en BD con constraint)
        updatePayload.is_household_account = isHouseholdAcc && isShared;
      }
      const { error } = await supabase.from("accounts").update({
        ...updatePayload,
      }).eq("id", editingId);
      if (error) {
        setIsSaving(false);
        return accountSaveError(error);
      }
      if (type === "credit_card") {
        if (ccDetailsReady) {
          await supabase.from("credit_card_details").upsert({
            account_id: editingId, closing_day: parseInt(closingDay), due_day: parseInt(dueDay), credit_limit: parseFloat(creditLimit) || 0,
          }, { onConflict: "account_id" });
        } else {
          toast.warning("No se pudo confirmar el día de corte, vencimiento o límite reales — no se modificaron. Reintentá desde Cuentas.");
        }
      }
      if (openingBalance !== "" && !isNaN(parseFloat(openingBalance))) {
        const balErr = await upsertOpeningBalance(editingId, parseFloat(openingBalance));
        if (balErr) { setIsSaving(false); return toast.error(balErr); }
      }
      toast.success("Cuenta actualizada");
    } else {
      // INSERT
      // En modo solo (sin pareja) forzamos shared para evitar fricciones.
      // En modo hogar respetamos lo que eligió el usuario.
      const willBeShared = isSolo ? true : isShared;
      const { data, error } = await supabase.from("accounts").insert({
        household_id: hh, name, type, currency, owner_id: profile.id, balance: 0,
        institution_code: normalizedInstitutionCode,
        is_shared: willBeShared,
        is_household_account: !isSolo && isHouseholdAcc && willBeShared,
        ...savingsFields,
      }).select().single();
      if (error || !data) {
        setIsSaving(false);
        return accountSaveError(error);
      }
      if (type === "credit_card") {
        await supabase.from("credit_card_details").insert({
          account_id: data.id, closing_day: parseInt(closingDay), due_day: parseInt(dueDay), credit_limit: parseFloat(creditLimit) || 0,
        });
      }
      const balNum = parseFloat(openingBalance);
      if (!isNaN(balNum) && balNum !== 0) {
        const balErr = await upsertOpeningBalance(data.id, balNum);
        if (balErr) {
          toast.error(balErr);
          // La cuenta ya se creó, no revertimos. El usuario puede ajustar el saldo manualmente.
        }
      }
      toast.success("Cuenta creada");
    }
    await refreshAccountQueries();
    setOpen(false);
    setIsSaving(false);
  }

  async function archiveAccount(id: string) {
    const { error } = await supabase.from("accounts").update({ is_active: false }).eq("id", id);
    if (error) return toastError(error);
    await refreshAccountQueries();
    setArchiveConfirmId(null);
    toast.success("Cuenta archivada");
  }

  async function unarchiveAccount(id: string) {
    const { error } = await supabase.from("accounts").update({ is_active: true }).eq("id", id);
    if (error) return toastError(error);
    await refreshAccountQueries();
    toast.success("Cuenta reactivada");
  }

  async function handleDeleteClick(id: string) {
    // El "Saldo inicial" lo creamos nosotros al crear la cuenta — no es un movimiento real del usuario.
    // Solo bloqueamos si hay movimientos reales (distintos de "Saldo inicial").
    const { count } = await supabase
      .from("transaction_headers")
      .select("id", { count: "exact", head: true })
      .eq("account_id", id)
      .neq("description", "Saldo inicial");
    if ((count ?? 0) > 0) {
      setHasMovementsAccountId(id);
      return;
    }
    setDeleteConfirmId(id);
  }

  async function deleteAccountHard(id: string) {
    // Limpiar el movimiento de "Saldo inicial" si existe (no es un movimiento real del usuario)
    const { data: saldoHeaders } = await supabase
      .from("transaction_headers")
      .select("id")
      .eq("account_id", id)
      .eq("description", "Saldo inicial");
    for (const h of saldoHeaders ?? []) {
      await supabase.from("transaction_lines").delete().eq("header_id", h.id);
      await supabase.from("transaction_headers").delete().eq("id", h.id);
    }
    await supabase.from("credit_card_details").delete().eq("account_id", id);
    const { error } = await supabase.from("accounts").delete().eq("id", id);
    if (error) return toastError(error);
    await refreshAccountQueries();
    setDeleteConfirmId(null);
    toast.success("Cuenta eliminada");
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Cuentas</h1>
        <Button onClick={openNew}><Plus className="mr-2 h-4 w-4" /> Nueva</Button>
      </div>
      {liquidityQ.data && liquidityQ.data.length > 0 && (
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {liquidityQ.data.map((row) => (
            <Card key={row.currency}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase text-muted-foreground">Safe to Spend</div>
                  <span className="text-[10px] rounded bg-muted px-1.5 py-0.5">{row.currency}</span>
                </div>
                <div className={"mt-1 text-2xl font-bold " + (row.safe_to_spend < 0 ? "text-expense" : "text-income")}>
                  {formatCurrency(row.safe_to_spend, row.currency)}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                  <div className="rounded border p-1.5">
                    <div className="text-[10px] text-muted-foreground">Líquido</div>
                    <div className="font-semibold tabular-nums">{formatCurrency(row.available, row.currency)}</div>
                  </div>
                  <div className="rounded border p-1.5">
                    <div className="text-[10px] text-muted-foreground">Deuda TC</div>
                    <div className="font-semibold tabular-nums text-expense">{formatCurrency(row.cc_debt_total, row.currency)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {(accQ.data ?? [])
          .filter((a) => isSolo ? true : accountScope(a as any, profile?.id, viewMode))
          .map((a) => (
          <Card key={a.id}><CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold flex items-center gap-1.5">
                  {a.name}
                  {(a as any).is_shared === false ? (
                    <span title="Cuenta privada · solo vos la ves" className="inline-flex">
                      <Lock className="h-3.5 w-3.5 text-warning" />
                    </span>
                  ) : isHouseholdAccount(a as any) ? (
                    <span title="Cuenta del hogar" className="inline-flex">
                      <Building2 className="h-3.5 w-3.5 text-primary" />
                    </span>
                  ) : !isSolo ? (
                    <span title="Compartida con el hogar" className="inline-flex">
                      <Home className="h-3.5 w-3.5 text-muted-foreground" />
                    </span>
                  ) : null}
                </div>
                <div className="text-xs uppercase text-muted-foreground">{a.type} · {a.currency}</div>
                {a.is_savings && <SavingsTag account={a} accounts={accQ.data ?? []} />}
                {(a.type === "bank" || a.type === "credit_card") && (
                  <div className="mt-0.5 text-[10px] text-muted-foreground">
                    {getFinancialInstitution(a.institution_code)?.label ?? (
                      <span className="text-amber-600 dark:text-amber-400">
                        Institución sin configurar
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-1">
                {renderBankConciliationButton(a, setReconcileAccount)}
                <Button size="icon" variant="ghost" onClick={() => setAdjustAccount(a)} title="Ajustar saldo real">
                  <Scale className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => openEdit(a)} title="Editar"><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setArchiveConfirmId(a.id)} title="Archivar cuenta"><Archive className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => handleDeleteClick(a.id)} title="Eliminar cuenta (solo si no tiene movimientos)"><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
            {a.type === "credit_card" ? (
              (() => {
                const debt = Number(debtQ.data?.get(a.id)?.debt ?? 0);
                return (
                  <div className="mt-3">
                    <div className={"text-2xl font-bold " + (debt > 0 ? "text-expense" : "")}>
                      {formatCurrency(debt, a.currency)}
                    </div>
                    <div className="text-[10px] text-muted-foreground">Deuda pendiente</div>
                  </div>
                );
              })()
            ) : (
              <div className={"mt-3 text-2xl font-bold " + (Number(a.balance) < 0 ? "text-expense" : "")}>
                {formatCurrency(Number(a.balance), a.currency)}
              </div>
            )}
          </CardContent></Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar cuenta" : "Nueva cuenta"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div><Label>Tipo</Label>
              <Select
                value={type}
                onValueChange={(v) => {
                  const nextType = v as AccountType;
                  setType(nextType);
                  if (nextType === "cash") setInstitutionCode("none");
                }}
                disabled={!!editingId}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank">Banco</SelectItem>
                  <SelectItem value="cash">Efectivo</SelectItem>
                  <SelectItem value="credit_card">Tarjeta de crédito</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(type === "bank" || type === "credit_card") && (
              <div>
                <Label>Institución financiera</Label>
                <Select value={institutionCode} onValueChange={setInstitutionCode}>
                  <SelectTrigger>
                    <SelectValue placeholder="Elegí una institución" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin institución configurada</SelectItem>
                    {financialInstitutionOptionsForAccountType(type).map((institution) => (
                      <SelectItem key={institution.code} value={institution.code}>
                        {institution.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Necesaria para conciliar PDFs. No se infiere del nombre de la cuenta.
                </p>
              </div>
            )}
            {savingsAllowed && (
              <div className="rounded-md border p-3 space-y-2">
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={isSavings}
                    onChange={(e) => {
                      setIsSavings(e.target.checked);
                      setSavingsParentId("");
                    }}
                  />
                  <span>
                    <span className="font-medium">Cuenta de ahorro (Cajita, Apartado)</span>
                    <span className="block text-xs text-muted-foreground">
                      Plata tuya que vive dentro de una cuenta del banco. Los traspasos a ella se
                      registran como transferencias, no como gasto.
                    </span>
                  </span>
                </label>
                {isSavings && (
                  <div>
                    <Label>Vive dentro de</Label>
                    <Select value={savingsParentId} onValueChange={setSavingsParentId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Elegí la cuenta del banco" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Ninguna</SelectItem>
                        {savingsParentOptions.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name} ({a.currency})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Sólo se ofrecen cuentas {savingsWillBeHousehold ? "del hogar" : "tuyas"}: un
                      ahorro no puede colgar de la cuenta de tu pareja.
                    </p>
                  </div>
                )}
              </div>
            )}
            <div><Label>Moneda</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!isSolo && (
              <div>
                <Label>Privacidad</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => setIsShared(true)}
                    className={"rounded-lg border-2 p-3 text-left transition " + (isShared ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}>
                    <div className="text-sm font-medium flex items-center gap-1.5"><Home className="h-4 w-4" /> Compartida</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Visible para tu pareja</div>
                  </button>
                  <button type="button" onClick={() => setIsShared(false)}
                    className={"rounded-lg border-2 p-3 text-left transition " + (!isShared ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}>
                    <div className="text-sm font-medium flex items-center gap-1.5"><Lock className="h-4 w-4" /> Privada</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Solo vos la ves</div>
                  </button>
                </div>
                {editingId && (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Si pasás una cuenta a privada, sus movimientos compartidos dejan de verse para tu pareja.
                  </p>
                )}
                {isShared && (
                  <button
                    type="button"
                    onClick={() => setIsHouseholdAcc((v) => !v)}
                    className={"mt-2 w-full rounded-lg border-2 p-3 text-left transition " + (isHouseholdAcc ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}
                  >
                    <div className="text-sm font-medium flex items-center gap-1.5">
                      <Building2 className="h-4 w-4" />
                      Cuenta del hogar
                      <span className={"ml-auto text-xs font-normal " + (isHouseholdAcc ? "text-primary" : "text-muted-foreground")}>
                        {isHouseholdAcc ? "Activada" : "No activada"}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Pertenece al hogar, no a ninguno de los dos en particular
                    </div>
                  </button>
                )}
              </div>
            )}
            <div className="rounded-md border bg-muted/30 p-3 space-y-2">
              <Label className="text-sm font-semibold">Saldo inicial / Ajuste</Label>
              <p className="text-xs text-muted-foreground">
                {editingId ? "Dejar vacío para no cambiar el saldo. Si pones un valor, se crea/actualiza un movimiento 'Saldo inicial' a la fecha indicada y los movimientos posteriores se respetan." : "Saldo de partida (ej. al 31/12 del año anterior)."}
              </p>
              {savingsActive && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Si la Cajita ya tiene plata, no la cargues como saldo inicial: registrá un
                  traspaso desde la cuenta, así el total del banco no se cuenta dos veces.
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Monto</Label><Input type="number" step="0.01" value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} placeholder="0.00" /></div>
                <div><Label className="text-xs">Fecha</Label><Input type="date" value={openingDate} onChange={(e) => setOpeningDate(e.target.value)} /></div>
              </div>
            </div>
            {type === "credit_card" && (
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Cierre</Label><Input type="number" min="1" max="31" value={closingDay} onChange={(e) => setClosingDay(e.target.value)} /></div>
                <div><Label>Vencimiento</Label><Input type="number" min="1" max="31" value={dueDay} onChange={(e) => setDueDay(e.target.value)} /></div>
                <div><Label>Límite</Label><Input type="number" step="0.01" value={creditLimit} onChange={(e) => setCreditLimit(e.target.value)} /></div>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={handleSave} disabled={isSaving}>{isSaving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <BankConciliationDialog account={reconcileAccount} onClose={closeReconciliation} />
      {/* Sección cuentas archivadas */}
      <div>
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground transition flex items-center gap-1.5"
        >
          <Archive className="h-3.5 w-3.5" />
          {showArchived ? "Ocultar cuentas archivadas" : "Mostrar cuentas archivadas"}
        </button>
        {showArchived && (
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
            {(archivedQ.data ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground col-span-3">No hay cuentas archivadas.</p>
            )}
            {(archivedQ.data ?? []).map((a) => (
              <Card key={a.id} className="opacity-60">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-sm">{a.name}</div>
                      <div className="text-xs uppercase text-muted-foreground">{a.type} · {a.currency}</div>
                      <div className="text-[10px] text-warning mt-0.5">Archivada</div>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => unarchiveAccount(a.id)} title="Reactivar cuenta">
                      <ArchiveRestore className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {adjustAccount && profile && (
        <BalanceAdjustDialog
          open={!!adjustAccount}
          onOpenChange={(v) => { if (!v) setAdjustAccount(null); }}
          account={adjustAccount}
          myId={profile.id}
        />
      )}

      {/* AlertDialog: confirmar archivado */}
      <AlertDialog open={!!archiveConfirmId} onOpenChange={(v) => { if (!v) setArchiveConfirmId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Archivar esta cuenta?</AlertDialogTitle>
            <AlertDialogDescription>
              La cuenta dejará de aparecer en la lista activa y no sumará al Safe to Spend, pero sus movimientos históricos se conservan y seguirán visibles en el historial. Podés reactivarla cuando quieras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => archiveConfirmId && archiveAccount(archiveConfirmId)}>
              Archivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog: confirmar eliminación permanente (solo sin movimientos) */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(v) => { if (!v) setDeleteConfirmId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta cuenta definitivamente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta cuenta no tiene movimientos, así que se puede eliminar de forma permanente. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteConfirmId && deleteAccountHard(deleteConfirmId)}
            >
              Eliminar definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog: aviso cuando tiene movimientos y el usuario intentó eliminar */}
      <AlertDialog open={!!hasMovementsAccountId} onOpenChange={(v) => { if (!v) setHasMovementsAccountId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Esta cuenta tiene movimientos</AlertDialogTitle>
            <AlertDialogDescription>
              No se puede eliminar una cuenta que tiene movimientos registrados, para no perder el historial. Podés <strong>archivarla</strong> en su lugar: dejará de aparecer como activa pero sus movimientos se conservarán visibles en el historial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              if (hasMovementsAccountId) {
                setArchiveConfirmId(hasMovementsAccountId);
                setHasMovementsAccountId(null);
              }
            }}>
              Archivar en cambio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
